import { DateTime } from "@bps/date-time";
import { ChangeLog, ChangeLogDto } from "@libs/api/types/common-dtos";
import { guid } from "@libs/common/guid";
import { RJSFSchema } from "@rjsf/utils";

export interface SoftwarePackageVersionConfig {
  id?: string;
  softwarePackageVersionId: string;
  key: string;
  defaultValue: string;
  description: string;
  dataType: string;
  createdDate?: DateTime;
  createdBy?: string;
}

export interface SoftwarePackageVersionConfigArgs {
  softwarePackageVersionId: string;
}

export interface SecurityRole {
  id: guid;
  code: string;
  isInactive: boolean;
  text: string;
  description: string;
  rolePermissions?: {
    permissionCode: string;
    source: string;
    RoleId: string;
  }[];
  isSystemManaged: boolean;
}

export interface FieldDeviceDetails
  extends Omit<
    FieldDeviceDetailsDto,
    | "controllerCreatedDateTimeUtc"
    | "controllerStartDateTimeUtc"
    | "controllerLastObservedDateTimeUtc"
    | "recoveryAgentCreatedDateTimeUtc"
    | "recoveryAgentStartDateTimeUtc"
    | "recoveryAgentLastObservedDateTimeUtc"
    | "tokenExpiryUtc"
  > {
  controllerCreatedDateTimeUtc?: DateTime;
  controllerStartDateTimeUtc?: DateTime;
  controllerLastObservedDateTimeUtc?: DateTime;
  recoveryAgentCreatedDateTimeUtc?: DateTime;
  recoveryAgentStartDateTimeUtc?: DateTime;
  recoveryAgentLastObservedDateTimeUtc?: DateTime;
  tokenExpiryUtc?: DateTime;
}

export interface FieldDeviceDetailsDto {
  id: guid;
  cloudConnectorId: guid;
  etag: string;
  changelog: ChangeLog;
  tokenExpiryUtc: string;
  computerName: string;
  machineGuid: string;
  osName: string;
  osVersion: string;
  systemDomain: string;
  timezone: string;
  maintenanceScriptVersion: string;
  controllerStatus: string;
  controllerRunStatusSid: string;
  controllerVersion: string;
  controllerStartup: string;
  controllerCreatedDateTimeUtc: string;
  controllerStartDateTimeUtc: string;
  controllerLastObservedDateTimeUtc: string;
  recoveryAgentStatus: string;
  recoveryAgentRunStatusSid: string;
  recoveryAgentVersion: string;
  recoveryAgentStartup: string;
  recoveryAgentCreatedDateTimeUtc: string;
  recoveryAgentStartDateTimeUtc: string;
  recoveryAgentLastObservedDateTimeUtc: string;
}

export interface FieldDevice {
  id: guid;
  cloudConnectorId: guid;
  etag: string;
  changeLog: ChangeLog;
  appRegistrationId?: guid;
  notBeforeTimeUtc?: DateTime;
  tokenExpiryUtc?: DateTime;
  authEnabled?: boolean;
  issueTimeUtc?: DateTime;
}

export interface FieldDeviceDto
  extends Omit<
    FieldDevice,
    "notBeforeTimeUtc" | "tokenExpiryUtc" | "issueTimeUtc" | "changeLog"
  > {
  notBeforeTimeUtc: string;
  tokenExpiryUtc: string;
  issueTimeUtc: string;
  changeLog: ChangeLogDto;
}

export interface FieldDeviceFilterArgs {
  searchTerm: string;
}

export interface RolloutPackageDesiredConfig {
  id?: string;
  rolloutPackageId: string;
  key: string;
  value: string;
  eTag?: string;
  changeLog?: ChangeLog;
}

export interface RolloutPackageDesiredConfigArgs {
  rolloutPackageId: string;
}

export interface RolloutPackageDesiredConfigCreateRequest {
  rolloutPackageId: string;
  key: string;
  value: string;
}

export interface RolloutPackageDesiredConfigUpdateRequest
  extends RolloutPackageDesiredConfigCreateRequest {
  id: string;
  eTag?: string;
  changeLog?: ChangeLog;
}

export interface RolloutPackage {
  id?: string;
  rolloutId: string;
  softwarePackageVersionId: string;
  eTag?: string;
  changeLog?: ChangeLog;
  rolloutPackageDesiredConfigs: RolloutPackageDesiredConfig[];
}

export interface RolloutPackageArgs {
  rolloutId: string;
}

export interface RolloutPackageCreateRequest {
  rolloutId: string;
  softwarePackageVersionId: string;
}

export interface RolloutPackageUpdateRequest {
  id: string;
  rolloutId: string;
  softwarePackageVersionId: string;
  eTag?: string;
}

export interface Rollout {
  id?: string;
  deploymentRingId: string;
  isActive: boolean;
  displayName: string;
  initialDownloadStartUtc?: DateTime;
  initialDownloadEndUtc?: DateTime;
  initialInstallStartUtc?: DateTime;
  initialInstallEndUtc?: DateTime;
  eTag?: string;
  rolloutPackages: RolloutPackage[];
}

export interface RolloutCreateRequest {
  rolloutId?: string;
  deploymentRingId: string;
  isActive: boolean;
  isBase64Encoded: boolean;
  displayName?: string;
  initialDownloadStartUtc: string | null;
  initialDownloadEndUtc: string | null;
  initialInstallStartUtc: string | null;
  initialInstallEndUtc: string | null;
  rolloutPackages?: RolloutPackagesCreateRequest[];
  rolloutPackagesDesiredConfigs?: RolloutPackagesDesiredConfigsCreateRequest[];
  eTag?: string;
}

export interface RolloutPackagesCreateRequest {
  rolloutPackageId?: string;
  softwarePackageVersionId: string;
  eTag?: string;
}

export interface RolloutPackagesDesiredConfigsCreateRequest {
  rolloutPackageDesiredConfigId?: string;
  softwarePackageVersionId: string;
  key: string;
  value?: string;
  eTag?: string;
}

export interface SoftwarePackage {
  id: guid;
  code: string;
  displayName: string;
  description: string;
  softwarePackageType: SoftwarePackageType;
  softwarePublisherId: guid;
  isActive: boolean;
}

export interface SoftwarePackageArgs {
  softwarePublisherCode?: string;
  softwarePackageType?: SoftwarePackageType;
  isActive?: boolean;
}

export enum SoftwarePackageType {
  Service = "SERVICE",
  Module = "MODULE",
  Application = "APPLICATION",
  File = "FILE",
  Bundle = "BUNDLE",
  Dependency = "DEPENDENCY"
}

export enum FieldDeviceCacheType {
  All = 0,
  Configuration = 1,
  Claims = 2,
  CloudConnectorId = 3,
  AuthFieldClientProfile = 4,
  FieldDevice = 5
}

export interface FieldDeviceCacheOption {
  type: FieldDeviceCacheType;
  text: string;
}

export enum SoftwarePackageServiceCodes {
  FieldController = "BestPracticeFieldController",
  RecoveryAgent = "BestPracticeFieldRecoveryAgent"
}

export interface FieldDeviceReportedConfig {
  fieldDeviceConfigReportId: string;
  fieldDeviceId: guid;
  softwarePackageType: SoftwarePackageType;
  softwarePackageCode: string;
  softwarePackageDisplayName: string;
  softwarePackageId: guid;
  softwarePackageVersion: string;
  softwarePackageVersionId?: guid;
  softwarePackageIsActive: boolean;
  softwarePublisherId: guid;
  softwarePublisherName: string;
  status: string;
  runStatus: string;
  startup: string;
  user: string;
  path: string;
  startDateTimeUtc: DateTime;
  createdDateTimeUtc: DateTime;
  lastObservedDateTimeUtc: DateTime;
  configurationSettings: ConfigurationSettings[];
}

export interface ConfigurationSettings {
  fieldDeviceConfigReportId?: guid;
  fieldDeviceConfigReportItemId: string;
  key?: string;
  value?: string;
}

export interface FieldDeviceReportedConfigDto
  extends Omit<
    FieldDeviceReportedConfig,
    "createdDateTimeUtc" | "startDateTimeUtc" | "lastObservedDateTimeUtc"
  > {
  startDateTimeUtc: string;
  createdDateTimeUtc: string;
  lastObservedDateTimeUtc: string;
}

export interface FieldDevicePropertyDto {
  id: number;
  fieldDeviceId?: guid;
  propertyType: string;
  propertyGroup?: string;
  propertyValue?: string;
  lastSourceType?: string;
  lastSourceId?: string;
  lastObservedUtc?: string;
}

export interface FieldDeviceClaimDto {
  id: string;
  key: string;
  value: string;
  eTag?: string;
  changeLog?: ChangeLogDto;
}

export type FieldDeviceClaim = Omit<FieldDeviceClaimDto, "changeLog"> & {
  changeLog?: ChangeLog;
};

export interface FieldDeviceProperty
  extends Omit<FieldDevicePropertyDto, "lastObservedUtc"> {
  lastObservedUtc?: DateTime;
}

export interface FieldDeviceIdentifierDto {
  id: number;
  fieldDeviceId?: guid;
  identifierType: string;
  identifierValue?: string;
  firstObservedUtc?: string;
  prevObservedUtc?: string;
  observationCount: number;
  lastSourceType?: string;
  lastSourceId?: string;
  lastObservedUtc?: string;
  authenticated: boolean;
}

export interface FieldDeviceIdentifier
  extends Omit<
    FieldDeviceIdentifierDto,
    "firstObservedUtc" | "prevObservedUtc" | "lastObservedUtc"
  > {
  firstObservedUtc?: DateTime;
  prevObservedUtc?: DateTime;
  lastObservedUtc?: DateTime;
}

export interface DeploymentRingFieldDevice {
  id: string;
  deploymentRingId: string;
  fieldDeviceId: string;
  sortOrder: number;
  eTag: string;
  changeLog?: ChangeLogDto;
}

export interface DeploymentRingFieldDeviceDetail
  extends DeploymentRingFieldDevice {
  name?: string;
  code: string;
  description?: string;
  isPublic: boolean;
}

export interface AddDeploymentRingFieldDeviceRequest {
  deploymentRingId: string;
  fieldDeviceId: string;
  sortOrder: number;
}

export interface FieldDeviceConfiguration {
  cloudConnectorId: guid;
  configurationVersion: string;
  configurationExpiryDate: DateTime;
  futureComponents: FieldDeviceConfigurationComponent[];
  currentComponents: FieldDeviceConfigurationComponent[];
  actions: FieldDeviceConfigurationAction[];
  options: FieldDeviceConfigOption;
}

export interface FieldDeviceConfigurationAction {
  arguments: string;
  toBeRunBy: string;
  cannotBeRunAfterUtc: DateTime;
  cannotBeRunBeforeUtc: DateTime;
  id: number;
  maximumWindowsVersion: string;
  minimumWindowsVersion: string;
}

export interface FieldDeviceConfigurationComponent {
  name: string;
  version: string;
  type: SoftwarePackageType;
  code: string;
  id: guid;
  dependencyCodes: string[];
  downloadUrl: string;
  configurationSettings: FieldDeviceConfigurationSettings[];
  executableName: string;
  expectedThumbprint: string;
  launchArguments: string;
  maximumWindowsVersion: string;
  minimumWindowsVersion: string;
  downloadWindow: {
    endUtc: DateTime;
    startUtc: DateTime;
  };
  installationWindow: {
    endUtc: DateTime;
    startUtc: DateTime;
  };
}

export interface FieldDeviceConfigurationSettings {
  dataType: string;
  key: string;
  value: string;
}
export interface FieldDeviceConfigurationOneTimeAction {
  arguments: string;
  toBeRunBy: string;
  cannotBeRunAfterUtc?: DateTime;
  cannotBeRunBeforeUtc?: DateTime;
  id: number;
  maximumWindowsVersion?: string;
  minimumWindowsVersion?: string;
}

export interface FieldDeviceConfigOption {
  applicationInsightsConnectionString: string;
  fieldGatewayApiBaseUrl: string;
  fieldGatewayEndpoint: string;
  fieldManagementApiBaseUrl: string;
  fieldEnvironment: string;
}

export interface FieldDeviceAuthAction {
  id: string;
  fieldDeviceId: string;
  deviceAction: string;
  deviceIdentifierType: string;
  deviceIdentifierValue: string;
  runDate?: DateTime;
  eTag: string;
  changeLog: ChangeLog;
}

export interface FieldDeviceSetting {
  id: string;
  fieldDeviceId: string;
  fieldDeviceSettingType: string;
  value?: string;
  eTag: string;
  changeLog: ChangeLog;
}

export type FieldDeviceSettingDto = Omit<FieldDeviceSetting, "changeLog"> & {
  changeLog: ChangeLogDto;
};

export type FieldDeviceAuthActionDto = Omit<
  FieldDeviceAuthAction,
  "changeLog" | "runDate"
> & {
  changeLog: ChangeLogDto;
  runDate?: string;
};

export interface FieldDeviceAuthActionRequest {
  fieldDeviceId: string;
  deviceAction: string;
  deviceIdentifierType: string;
  deviceIdentifierValue: string;
}

export interface DeviceConfigurationDiff {
  desiredConfig: DiffSoftwarePackage[];
  reportedConfig: DiffSoftwarePackage[];
}

export interface DiffSoftwarePackage {
  softwarePackageCode: string;
  version: string;
  settings: DiffSoftwarePackageConfigurationSetting[];
}

export interface DiffSoftwarePackageConfigurationSetting {
  key: string;
  value: string;
}

export interface DeploymentRing {
  id: guid;
  code: string;
  isInactive: string;
  isPublic: boolean;
  displayName: string;
  description: string;
  selectorId?: guid;
  changeLog: ChangeLog;
  eTag?: string;
}

export interface DeploymentRingCreateRequest {
  code: string;
  isPublic: boolean;
  displayName: string;
  description: string;
}

export interface DeploymentRingUpdateRequest {
  code: string;
  isPublic: boolean;
  displayName: string;
  description: string;
  deploymentRingId: guid;
  selectorId?: guid;
  eTag: string;
}

export interface CustomRJSFSchema extends RJSFSchema {
  "x-messageType": string;
  "x-permission": string;
  "x-command-category": string;
}

export enum ChannelPreference {
  Queued = "Queued",
  Realtime = "Realtime",
  RealtimeWithQueuedFallback = "RealtimeWithQueuedFallback"
}

export enum TargetConfigurationType {
  Reported = "Reported",
  Desired = "Desired"
}

export enum FieldDeviceRoleType {
  Client = "CLIENT",
  Server = "SERVER"
}

export interface DeviceCommand {
  fieldDeviceId?: string;
  channelPreference?: string;
  targetSoftwarePackageCode: string;
  routedMessageData: RoutedMessageData;
}

export interface DeploymentRingIdDto {
  deploymentRingId: string;
}

export interface BulkAssignTenantDeploymentRingRequest {
  clientDeploymentRingIdList?: DeploymentRingIdDto[];
  serverDeploymentRingIdList?: DeploymentRingIdDto[];
  selectorId: string;
}

export interface BulkAssignDeploymentRingRequest {
  deploymentRingId: string;
  selectorId: string;
  assignHighestPriority: boolean;
}

export interface BulkAuthActionRequest {
  selectorId: string;
  deviceAction: string;
}

export interface BulkCommandDispatchRequest {
  selectorId: string;
  smartDeviceCommandMessage: DeviceCommand;
  targetConfigurationType: TargetConfigurationType;
}

export interface BulkActionResponse {
  correlationId?: string;
  deviceCount?: number;
  errorCount?: number;
  processedCount?: number;
}

export interface RoutedMessageData {
  messageType: string;
  value: string;
}

export interface CommandResponse {
  Status?: number;
  Value?: string;
  ValueType?: string;
  ErrorMessage?: string;
  CorrelationId?: string;
}

export interface SelectorArgs {
  selectorId?: string;
  name?: string;
}

export interface SelectorCreateRequest {
  name: string;
  description: string;
  rules: SelectorCreateRequestRule[];
}

export interface SelectorCreateRequestRule {
  attributeType?: string;
  valueDataType?: string;
  comparisonOperator?: string;
  value?: string;
}

export type SelectorRule = {
  id?: guid;
  selectorId?: guid;
  attributeType?: string;
  valueDataType?: string;
  comparisonOperator?: string;
  value?: string;
  etag?: string;
  changeLog?: ChangeLog;
};

export interface SelectorRuleDto extends Omit<SelectorRule, "changeLog"> {
  changeLog: ChangeLogDto;
}

export type Selector = {
  id: guid;
  name: string;
  description?: string;
  deviceCount: string;
  etag?: string;
  isSystemManaged: boolean;
  changeLog?: ChangeLog;
  rules: SelectorRule[];
};

export interface SelectorDto extends Omit<Selector, "changeLog" | "rules"> {
  changeLog: ChangeLogDto;
  rules: SelectorRuleDto[];
}

export enum ComparisonOperator {
  Equals = "=",
  Like = "LIKE"
}

export enum KnownDeviceProperties {
  SiteID = "PremierDatabase_SiteID"
}

export type DeviceSummary = {
  cloudConnectorId: string;
  id: string;
  computerName: string;
  installType: number;
  siteId: number;
  siteName: string;
};

export interface SoftwarePackageDefault {
  id: guid;
  softwarePackageId: guid;
  softwarePackageVersionId: guid;
  changeLog: ChangeLog;
  eTag?: string;
}

export interface SoftwarePackageDefaultRequest {
  softwarePackageId: guid;
  softwarePackageVersionId: guid;
}

export interface RolloutDetailDto
  extends Omit<
    RolloutDetail,
    | "initialDownloadStartUtc"
    | "initialDownloadEndUtc"
    | "initialInstallStartUtc"
    | "initialInstallEndUtc"
  > {
  initialDownloadStartUtc: string;
  initialDownloadEndUtc: string;
  initialInstallStartUtc: string;
  initialInstallEndUtc: string;
}

export interface DeploymentRingDetailDto
  extends Omit<DeploymentRingDetail, "rollouts"> {
  rollouts: RolloutDetailDto[];
}

export interface DeploymentDetailDto
  extends Omit<DeploymentDetail, "deploymentRings"> {
  deploymentRings: DeploymentRingDetailDto[];
}

export interface DeploymentDetail {
  ratio: number;
  deploymentRings: DeploymentRingDetail[];
  defaultVersions: DefaultVersionDetail[];
}

export type DeploymentRingAssignmentType = "Manual" | "Tenant";

export interface DeploymentRingDetail {
  deploymentRingId: guid;
  deploymentRingCode: string;
  deploymentRingDescription?: string;
  deploymentRingDisplayName: string;
  assignmentSource: DeploymentRingAssignmentType;
  rollouts: RolloutDetail[];
}

export interface DeviceAdvancedSearchArgs {
  rules: DeviceAdvancedSearchRuleArgs[];
}

export interface DeviceAdvancedSearchRuleArgs {
  attributeType?: string;
  valueDataType?: string;
  comparisonOperator?: string;
  value?: string;
}

export interface DeviceDetail extends DeviceSummary {
  identifiers: FieldDeviceIdentifier[];
  properties: FieldDeviceProperty[];
}

export interface DeviceDetailDto
  extends Omit<DeviceDetail, "identifiers" | "properties"> {
  identifiers: FieldDeviceIdentifierDto[];
  properties: FieldDevicePropertyDto[];
}

export interface RolloutDetail {
  rolloutId: guid;
  deploymentRingId: guid;
  displayName: string;
  initialDownloadStartUtc?: DateTime;
  initialDownloadEndUtc?: DateTime;
  initialInstallStartUtc?: DateTime;
  initialInstallEndUtc?: DateTime;
  rolloutPackages: RolloutPackageDetail[];
}

export interface RolloutPackageDetail {
  rolloutId: guid;
  softwarePackageId: guid;
  softwarePackageName: string;
  softwarePackageVersionId: guid;
  version: string;
}

export interface DefaultVersionDetail {
  softwarePackageId: guid;
  softwarePackageName: string;
  softwarePackageVersionId: guid;
  version: string;
}

export interface SoftwarePackageVersion {
  id: guid;
  certificateThumbprint: string;
  datePublishedUtc: DateTime;
  executableName: string;
  launchArguments: string;
  maximumWindowsVersion: string;
  minimumWindowsVersion: string;
  softwarePackageId: guid;
  softwarePackageCode: string;
  softwarePackageDisplayName: string;
  softwarePackageDescription: string;
  softwarePackageTypeCode: string;
  softwarePackageTypeText: string;
  url: string;
  version: string;
  virusScanIssueCount: number;
  softwarePublisherCode: string;
  softwarePublisherDescription: string;
  softwarePublisherDisplayName: string;
  softwarePublisherId: string;
}

export interface SoftwarePackageVersionDto
  extends Omit<SoftwarePackageVersion, "datePublishedUtc"> {
  datePublishedUtc: string;
}

export interface SoftwarePackageVersionArgs {
  datePublishedFrom?: string;
  datePublishedTo?: string;
  softwarePackageId?: string;
  softwarePublisherId?: string;
  softwarePackageType?: string;
  version?: string;
}

export interface SoftwarePublisher {
  id: guid;
  code: string;
  description: string;
  displayName: string;
}

export interface CombinedSoftwarePackage {
  id: guid;
  softwarePackageCode: string;
  softwarePackageType: SoftwarePackageType;
  currentVersion?: string;
  desiredVersion?: string;
  name: string;
}

export interface SoftwarePackageLogArgs {
  fieldDeviceId: guid;
  searchKeyword?: string;
}

export interface SoftwarePackageLogDto {
  blobDownloadUrl: string;
  blobName: string;
  blobFileSize: number;
  blobCreationDate: string;
  blobLastModifiedDate: string;
  logFileDate: string;
}

export interface SoftwarePackageLog
  extends Omit<
    SoftwarePackageLogDto,
    "blobCreationDate" | "blobLastModifiedDate" | "logFileDate"
  > {
  blobCreationDate?: DateTime;
  blobLastModifiedDate?: DateTime;
  logFileDate?: DateTime;
}

export enum InternalIdentifierTypes {
  Id = "Id" // Field device ID
}

export enum IdentifierTypes {
  BaseBoard_SerialNumber = "BaseBoard_SerialNumber",
  Bios_SerialNumber = "Bios_SerialNumber",
  CloudConnectorId = "CloudConnectorId",
  ComputerSystemProduct_IdentifyingNumber = "ComputerSystemProduct_IdentifyingNumber",
  ComputerSystemProduct_UUID = "ComputerSystemProduct_UUID",
  HaloRegistry_HaloGuid = "HaloRegistry_HaloGuid",
  MachineGuid = "MachineGuid",
  NTDomain_DomainGuid = "NTDomain_DomainGuid",
  OperatingSystem_SerialNumber = "OperatingSystem_SerialNumber",
  Processor_ProcessorId = "Processor_ProcessorId"
}

export enum PropertyTypes {
  CloudConnector_Profile = "CloudConnector_Profile",
  ComputerName = "ComputerName",
  ComputerSystem_Domain = "ComputerSystem_Domain",
  ComputerSystem_HypervisorPresent = "ComputerSystem_HypervisorPresent",
  ComputerSystem_PartOfDomain = "ComputerSystem_PartOfDomain",
  CurrentUserDomain = "CurrentUserDomain",
  DomainAdminGroup_SID = "DomainAdminGroup_SID",
  FieldDeviceGroupId = "FieldDeviceGroupId",
  IPV4Address = "IPV4Address",
  MaintScriptVersion = "MaintScriptVersion",
  NASHCertificate_ExpiryDate = "NASHCertificate_ExpiryDate",
  NetworkAdapterConfig_IPAddress = "NetworkAdapterConfig_IPAddress",
  OperatingSystem_BuildNumber = "OperatingSystem_BuildNumber",
  OperatingSystem_Caption = "OperatingSystem_Caption",
  OperatingSystem_Locale = "OperatingSystem_Locale",
  OperatingSystem_OSArchitecture = "OperatingSystem_OSArchitecture",
  OperatingSystem_OSLanguage = "OperatingSystem_OSLanguage",
  OperatingSystem_Version = "OperatingSystem_Version",
  PremierDatabase_PracticeName = "PremierDatabase_PracticeName",
  PremierDatabase_PremierSqlServer = "PremierDatabase_PremierSqlServer",
  PremierDatabase_PremierVersion = "PremierDatabase_PremierVersion",
  PremierDatabase_SiteID = "PremierDatabase_SiteID",
  PremRegistryGeneral_InstallType_32 = "PremRegistryGeneral_InstallType_32",
  PremRegistryGeneral_InstallType_64 = "PremRegistryGeneral_InstallType_64",
  PremRegistryGeneral_SiteID_32 = "PremRegistryGeneral_SiteID_32",
  PremRegistryGeneral_SiteID_64 = "PremRegistryGeneral_SiteID_64",
  PremRegistryGeneral_Version_32 = "PremRegistryGeneral_Version_32",
  PremRegistryGeneral_Version_64 = "PremRegistryGeneral_Version_64",
  Profile = "Profile",
  TimeZone_Bias = "TimeZone_Bias",
  TimeZone_Caption = "TimeZone_Caption",
  VerboseLogging = "VerboseLogging",
  xFFIPV4Address = "xFFIPV4Address"
}

export enum KnownClaimKeys {
  ServerTenantId = "TenantId",
  ClientTenantId = "ClientTenantId",
  ServerExternalEncodingId = "ENCID",
  ClientExternalEncodingId = "ClientENCID"
}

export enum FieldConfigurationVersion {
  V3_0 = "3.0",
  V2_0 = "2.0"
}

export enum DeviceSearchSelectorCreateType {
  FromDevices = "FromDevices",
  FromCriteria = "FromCriteria"
}

export interface TenantDeploymentRing {
  id: guid;
  fieldDeviceRoleTypeSid: string;
  tenantId: string;
  deploymentRingId: string;
  sortOrder: number;
  etag: string;
  changeLog?: ChangeLog;
}

export interface TenantDeploymentRingSummary extends TenantDeploymentRing {
  deploymentRingCode: string;
  deploymentRingDisplayName: string;
  deploymentRingDescription: string;
  id: guid;
  fieldDeviceRoleTypeSid: string;
  tenantId: string;
  deploymentRingId: string;
  sortOrder: number;
  etag: string;
  changeLog: ChangeLog;
}

export type TenantDeploymentRingRequest = {
  fieldDeviceRoleTypeSid: string;
  tenantId: string;
  deploymentRingId: string;
  sortOrder: number;
};

export interface TenantDeploymentRingSummaryDto
  extends Omit<TenantDeploymentRingSummary, "changeLog"> {
  changeLog: ChangeLogDto;
}

export type TenantDeploymentRingGetArgs = {
  tenantId: guid;
  deploymentRingId: guid;
  fieldDeviceRoleTypeSid: string;
  fieldDeviceId: guid;
};
